import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronDown, Save } from "lucide-react";
import { toast } from "sonner";

const settingsSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  domain: z.string().optional(),
  billing_email: z.string().email("Invalid email").optional().or(z.literal("")),
  billing_via_agency: z.boolean(),
  plan_id: z.string().optional(),
  max_users: z.number().min(1).max(1000).optional().nullable(),
  max_phone_numbers: z.number().min(1).max(100).optional().nullable(),
  max_sms_per_day: z.number().min(1).max(100000).optional().nullable(),
});

type SettingsFormData = z.infer<typeof settingsSchema>;

interface Tenant {
  tenant_id: string;
  name: string;
  slug: string | null;
  domain: string | null;
  status: string | null;
  plan_id: string | null;
  max_users: number | null;
  max_phone_numbers: number | null;
  max_sms_per_day: number | null;
  billing_email: string | null;
  billing_via_agency: boolean | null;
}

interface CustomerSettingsTabProps {
  tenant: Tenant;
}

const PLANS = [
  { id: "starter", name: "Starter" },
  { id: "pro", name: "Pro" },
  { id: "enterprise", name: "Enterprise" },
];

export function CustomerSettingsTab({ tenant }: CustomerSettingsTabProps) {
  const [showLimits, setShowLimits] = useState(false);
  const queryClient = useQueryClient();

  const form = useForm<SettingsFormData>({
    resolver: zodResolver(settingsSchema),
    defaultValues: {
      name: tenant.name,
      domain: tenant.domain || "",
      billing_email: tenant.billing_email || "",
      billing_via_agency: tenant.billing_via_agency ?? true,
      plan_id: tenant.plan_id || "",
      max_users: tenant.max_users,
      max_phone_numbers: tenant.max_phone_numbers,
      max_sms_per_day: tenant.max_sms_per_day,
    },
  });

  // Reset form when tenant changes
  useEffect(() => {
    form.reset({
      name: tenant.name,
      domain: tenant.domain || "",
      billing_email: tenant.billing_email || "",
      billing_via_agency: tenant.billing_via_agency ?? true,
      plan_id: tenant.plan_id || "",
      max_users: tenant.max_users,
      max_phone_numbers: tenant.max_phone_numbers,
      max_sms_per_day: tenant.max_sms_per_day,
    });
  }, [tenant, form]);

  const updateMutation = useMutation({
    mutationFn: async (data: SettingsFormData) => {
      const { data: result, error } = await supabase.functions.invoke(
        "Calie_updateTenant",
        {
          body: {
            tenant_id: tenant.tenant_id,
            name: data.name,
            domain: data.domain || null,
            billing_email: data.billing_email || null,
            billing_via_agency: data.billing_via_agency,
            plan_id: data.plan_id || null,
            max_users: data.max_users,
            max_phone_numbers: data.max_phone_numbers,
            max_sms_per_day: data.max_sms_per_day,
          },
        }
      );
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenant", tenant.tenant_id] });
      toast.success("Settings saved successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to save settings");
    },
  });

  const onSubmit = (data: SettingsFormData) => {
    updateMutation.mutate(data);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Business Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <FormItem>
                <FormLabel>Slug</FormLabel>
                <Input value={tenant.slug || ""} disabled />
                <FormDescription>
                  Slug cannot be changed after creation
                </FormDescription>
              </FormItem>

              <FormField
                control={form.control}
                name="domain"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Domain</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="example.com" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Billing */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Billing</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="billing_email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Billing Email</FormLabel>
                  <FormControl>
                    <Input {...field} type="email" placeholder="billing@example.com" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="billing_via_agency"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Bill Through Agency</FormLabel>
                    <FormDescription>
                      Include this customer's usage in your agency invoice
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Plan & Limits */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Plan & Limits</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="plan_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Plan</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || ""}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a plan" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {PLANS.map((plan) => (
                        <SelectItem key={plan.id} value={plan.id}>
                          {plan.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Collapsible open={showLimits} onOpenChange={setShowLimits}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" className="w-full justify-between">
                  Custom Limit Overrides
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${
                      showLimits ? "rotate-180" : ""
                    }`}
                  />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-4 pt-4">
                <div className="grid gap-4 sm:grid-cols-3">
                  <FormField
                    control={form.control}
                    name="max_users"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Max Users</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            {...field}
                            value={field.value ?? ""}
                            onChange={(e) =>
                              field.onChange(
                                e.target.value ? parseInt(e.target.value) : null
                              )
                            }
                            placeholder="Unlimited"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="max_phone_numbers"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Max Phone Numbers</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            {...field}
                            value={field.value ?? ""}
                            onChange={(e) =>
                              field.onChange(
                                e.target.value ? parseInt(e.target.value) : null
                              )
                            }
                            placeholder="Unlimited"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="max_sms_per_day"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Max SMS/Day</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            {...field}
                            value={field.value ?? ""}
                            onChange={(e) =>
                              field.onChange(
                                e.target.value ? parseInt(e.target.value) : null
                              )
                            }
                            placeholder="Unlimited"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CollapsibleContent>
            </Collapsible>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" disabled={updateMutation.isPending}>
            <Save className="mr-2 h-4 w-4" />
            {updateMutation.isPending ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
